import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import ReusableTable from "components/Tables/ReusableTable";
import { Flex, Box, Text, Badge } from "@chakra-ui/react";
import { EllipsisVertical, Package, Wrench } from "lucide-react";
import ReusableDrawer from "../../../components/Drawers/ReusableDrawer.js";
import ViewPackageDetails from "./components/ViewPackageDetails";
import ManagePackage from "./components/ManagePackage.js";
import getStatusColor from "./utils/StatusColor.js";
import ThreeDotMenu from "components/Menus/ThreeDotMenu.js";
import { fetchPackagesAction } from "../../../state/actions/packageActions";
import { formatDate } from "utils/dateFormatter";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import LoadingSpinner from "components/Loading/LoadingSpinner.js";

const PAGE_SIZE = 20;

const PackageManagement = () => {
  const dispatch = useDispatch();
  const { packages, loading, error } = useSelector((state) => state.packages || {});
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [isViewPackageDrawerOpen, setViewPackageDrawerOpen] = useState(false);
  const [isManagePackageDrawerOpen, setManagePackageDrawerOpen] = useState(false);
  const isLoadingInitial = loading && page === 1;
const isLoadingMore = loading && page > 1;
  const observerRef = useRef();

  // Fetch packages for the current page
  const fetchMorePackages = useCallback(async () => {
    const res = await dispatch(fetchPackagesAction(null, null, page, PAGE_SIZE));
    if (res.success) {
      if (res.data.length < PAGE_SIZE) {
        setHasMore(false);
      } else {
        setPage((prev) => prev + 1);
      }
    } else {
      setHasMore(false);
    }
  }, [dispatch, page]);

  // Intersection observer callback to trigger loading more data
  const lastRowRef = useCallback(
    (node) => {
      if (loading || !hasMore) return;
      if (observerRef.current) observerRef.current.disconnect();
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          fetchMorePackages();
        }
      });
      if (node) observerRef.current.observe(node);
    },
    [loading, hasMore, fetchMorePackages]
  );

  // Initial fetch on mount
  useEffect(() => {
    dispatch(fetchPackagesAction(null, null, 1, PAGE_SIZE));
  }, [dispatch]);

  // Prepare table data from packages
  const tableData = useMemo(() => {
    if (!Array.isArray(packages)) return [];
    return packages.map((pkg, idx) => {
      const images = Array.isArray(pkg.package_images)
        ? pkg.package_images.map((img) => `/${img}`)
        : [];
      const senderName = pkg.sender
        ? `${pkg.sender.first_name} ${pkg.sender.last_name}`
        : pkg.sender_id;
      const recipientName = pkg.recipient
        ? `${pkg.recipient.first_name} ${pkg.recipient.last_name}`
        : pkg.recipient_id;

      return {
        packageId: pkg.package.package_id,
        description: pkg.package.package_description,
        images,
        sender: senderName,
        recipient: recipientName,
        packageNumber: pkg.package.package_number,
        status: pkg.package.package_status,
        pickup: pkg.package.pickup_location,
        dropoff: pkg.package.dropoff_location,
        type: pkg.package.delivery_type,
        createdAt: formatDate(pkg.package.created_at),
        price: pkg.package.price ? `P${pkg.package.price.toFixed(2)}` : "N/A",
        dimensions: `${pkg.package.package_length}×${pkg.package.package_width}×${pkg.package.package_height} cm`,
        weight: `${pkg.package.package_weight} kg`,
        original: pkg,
        // **Do NOT pass ref here**. We handle last row ref separately.
      };
    });
  }, [packages]);

  // Columns definition
  const columnsData = [
    {
      Header: "Item",
      accessor: "description",
      width: "10%",
    },
    {
      Header: "Price",
      accessor: "price",
      width: "15%",
    },
    {
      Header: "Route",
      accessor: "route",
      Cell: ({ row }) => (
        <Box>
          <Text>
            {row.original.pickup} → {row.original.dropoff}
          </Text>
        </Box>
      ),
      width: "20%",
    },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ value }) => (
        <Badge colorScheme={getStatusColor(value)} borderRadius="full" px={2}>
          {value}
        </Badge>
      ),
      width: "10%",
    },
    {
      Header: "Sender",
      accessor: "sender",
      Cell: ({ row }) => <Text>{row.original.sender}</Text>,
      width: "15%",
    },
    {
      Header: "Recipient",
      accessor: "recipient",
      Cell: ({ row }) => <Text>{row.original.recipient}</Text>,
      width: "15%",
    },
    {
      Header: "Created",
      accessor: "createdAt",
      width: "10%",
    },
    {
      Header: "Actions",
      accessor: "moreOptions",
      Cell: ({ row }) => (
        <ThreeDotMenu
          icon={<EllipsisVertical size={20} color="grey" />}
          items={[
            {
              icon: <Package size={16} />,
              text: "View Package",
              onClick: () => handleViewPackage(row.original),
            },
            {
              icon: <Wrench size={16} />,
              text: "Manage Package",
              onClick: () => handleManagePackage(row.original),
            },
          ]}
        />
      ),
      width: "5%",
    },
  ];

  // Handlers for menu actions
  const handleViewPackage = (pkg) => {
    setSelectedPackage(pkg);
    setViewPackageDrawerOpen(true);
  };

  const handleManagePackage = (pkg) => {
    setSelectedPackage(pkg);
    setManagePackageDrawerOpen(true);
  };

 return (
  <Flex flexDirection="column" pt={{ base: "120px", md: "75px" }}>
    <Box p={6}>
      <Flex justifyContent="space-between" alignItems="center" mb={4}>
        {isLoadingInitial ? (
          <Skeleton height="20px" width="120px" />
        ) : (
          <Text fontSize="md" fontWeight="bold">
            All Packages: {packages?.length ?? 0}
          </Text>
        )}
      </Flex>
    </Box>

    <ReusableTable
      columnsData={columnsData}
      tableData={tableData}
      isLoading={isLoadingInitial}
      error={error}
      lastRowRef={lastRowRef}
    />

    {isLoadingMore && <LoadingSpinner mt={4} py={2} />}

    <ReusableDrawer
      isOpen={isViewPackageDrawerOpen}
      onClose={() => setViewPackageDrawerOpen(false)}
      headerContent={
        selectedPackage && (
          <Text fontSize="lg" fontWeight="bold">
            View Package - {selectedPackage.packageNumber} {selectedPackage.description}
          </Text>
        )
      }
      bodyContent={
        selectedPackage && (
          <ViewPackageDetails
            package={selectedPackage.original.package}
            sender={selectedPackage.original.sender}
            recipient={selectedPackage.original.recipient}
            onClose={() => setViewPackageDrawerOpen(false)}
          />
        )
      }
      size="lg"
    />

    <ReusableDrawer
      isOpen={isManagePackageDrawerOpen}
      onClose={() => setManagePackageDrawerOpen(false)}
      headerContent={
        selectedPackage && (
          <Text fontSize="lg" fontWeight="bold">
            Manage Package - #{selectedPackage.packageNumber}
          </Text>
        )
      }
      bodyContent={
        selectedPackage && (
          <ManagePackage package={selectedPackage} onClose={() => setManagePackageDrawerOpen(false)} />
        )
      }
      size="lg"
    />
  </Flex>
);

};

export default PackageManagement;
